import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { DraftFunction } from 'use-immer';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { LoqateValidationFields } from '@/core/types/Loqate';
import { WizardValidationError } from '@/core/types/Wizard';
import { RapidStorSettings } from '@/core/utils/rapidstor';
import { getAutobillFields } from '@/core/utils/validation';

import CircleCheckbox from '@/core/components/elements/circle-checkbox';
import ErrorTag from '@/core/components/elements/error-tag';

import classes from './styles.module.scss';

import { CardExpiry } from '../payment-form';

interface Props {
    form: Record<string, string>;
    update: (
        arg: Record<string, string> | DraftFunction<Record<string, string>>,
    ) => void;
    loqateValidation: (field: string) => void;
    updateExpiryDate: (
        values: Record<string, string | number>,
        autobill?: boolean,
    ) => void;
    loqateLoading?: LoqateValidationFields;
    validationErrors?: WizardValidationError[];
    bankName: string | undefined;
}

export default function AutoBill({
    form,
    update,
    loqateValidation,
    updateExpiryDate,
    loqateLoading,
    validationErrors,
    bankName,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const autobill = RapidStorSettings(location).autobill();
    const { moveinSingle } = location.rapidstor.wizards;

    const formErrors = useMemo(() => {
        const inputNames = getAutobillFields(
            location,
            form.autobillType,
            form.autobillUsePaymentDetails === 'true',
        );

        return validationErrors?.filter((error) => inputNames.includes(error.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validationErrors, form.autobillType, form.autobillUsePaymentDetails]);

    const errorTags = formErrors?.map((error) => (
        <ErrorTag key={`${error.name}_error_idx`} error={error} />
    ));

    const directDebitFields = (
        <>
            <div className={`${classes.directDebitDetails}`}>
                <input
                    type="text"
                    onChange={(e) => {
                        if (e.currentTarget.value.length <= 6) {
                            update((draft) => {
                                draft.autobillBsbNumber = e.target.value;
                            });
                        }
                    }}
                    value={form.autobillBsbNumber}
                    disabled={loqateLoading === 'bank'}
                    className={
                        formErrors?.some((error) => error.name === 'bank')
                            ? classes.invalid
                            : ''
                    }
                    onBlur={() => loqateValidation('bank')}
                    placeholder={t('wizard:autobillDetails:bsbNumber')}
                />
                <input
                    type="text"
                    onChange={(e) => {
                        if (e.currentTarget.value.length <= 9) {
                            update((draft) => {
                                draft.autobillAccountNumber = e.target.value;
                            });
                        }
                    }}
                    value={form.autobillAccountNumber}
                    className={
                        formErrors?.some((error) => error.name === 'bank')
                            ? classes.invalid
                            : ''
                    }
                    disabled={loqateLoading === 'bank'}
                    onBlur={() => loqateValidation('bank')}
                    placeholder={t('wizard:autobillDetails:accountNumber')}
                />
            </div>
            <div className={classes.autobillBox} style={{ marginTop: 16 }}>
                <div
                    id="custom-location-content-direct-debit"
                    dangerouslySetInnerHTML={{
                        __html: moveinSingle.directDebitContent || '',
                    }}
                />
            </div>
        </>
    );

    const creditCardManualFields = (
        <div className={`${classes.inputs}`}>
            <div>
                <input
                    type="text"
                    placeholder="Card Holder Name*"
                    required
                    value={form.autobillCreditCardName}
                    onChange={(e) => {
                        update((draft) => {
                            draft.autobillCreditCardName = e.target.value;
                        });
                    }}
                    name="cardHolderName"
                />
            </div>

            <div>
                <input
                    type="text"
                    placeholder="Card Number*"
                    required
                    value={form.autobillCreditCardNumber}
                    onChange={(e) => {
                        update((draft) => {
                            draft.autobillCreditCardNumber = e.target.value;
                        });
                    }}
                    name="cardNumber"
                />
            </div>

            <div>
                <CardExpiry
                    required
                    name="cardExpiry"
                    onValueChange={(values: Record<string, string | number>) => {
                        updateExpiryDate(values, true);
                    }}
                    month={form.autobillCreditCardExpiryMonth}
                    year={form.autobillCreditCardExpiryYear}
                />
            </div>

            <div>
                <input
                    type="text"
                    placeholder="CSV*"
                    name="cardCsv"
                    value={form.autobillCreditCardCsv}
                    onChange={(e) => {
                        update((draft) => {
                            draft.autobillCreditCardCsv = e.target.value;
                        });
                    }}
                    required
                />
            </div>
        </div>
    );

    const creditCardBlock = (
        <>
            {!autobill.allowSwitchingToDebit()
                && t('wizard:autobillDetails:switchToDebit') && (
                <p>{t('wizard:autobillDetails:switchToDebit')}</p>
            )}
            {!autobill.forceInitialCard() && (
                <CircleCheckbox
                    label="Use Payment Details"
                    value={form.autobillUsePaymentDetails}
                    formKey="usePaymentDetails"
                    update={() => {
                        update((draft) => {
                            draft.autobillUsePaymentDetails = draft.autobillUsePaymentDetails === 'true'
                                ? 'false'
                                : 'true';
                        });
                    }}
                />
            )}
            {form.autobillUsePaymentDetails === 'false' && creditCardManualFields}
            <div className={classes.autobillBox} style={{ marginTop: 16 }}>
                <div
                    id="custom-location-content-credit-card"
                    dangerouslySetInnerHTML={{
                        __html: moveinSingle.creditCardContent || '',
                    }}
                />
            </div>
        </>
    );

    return (
        <div className={`${classes.container}`}>
            <span className={`${classes.header_text}`}>
                {t('wizard:autobillDetails:title')}
            </span>

            <span
                className={`${classes.subtitle}`}
                dangerouslySetInnerHTML={{
                    __html: moveinSingle.autobillContent,
                }}
            />

            {autobill.allowDirectDebit() ? (
                <>
                    <div className={`${classes.autobill_type}`}>
                        <CircleCheckbox
                            label={t('wizard:autobillDetails:directDebit')}
                            value={form.autobillType === 'dd' ? 'true' : 'false'}
                            update={() => {
                                update((draft) => {
                                    draft.autobillType = 'dd';
                                });
                            }}
                            formKey="autobill_dd"
                        />

                        {autobill.allowCreditCard() && (
                            <CircleCheckbox
                                label={t('wizard:autobillDetails:creditCard')}
                                value={form.autobillType === 'cc' ? 'true' : 'false'}
                                update={() => {
                                    update((draft) => {
                                        draft.autobillType = 'cc';
                                        if (autobill.forceInitialCard()) {
                                            draft.autobillUsePaymentDetails = 'true';
                                        }
                                    });
                                }}
                                formKey="autobill_cc"
                            />
                        )}

                        {!autobill.required() && (
                            <CircleCheckbox
                                label={t('wizard:autobillDetails:none')}
                                value={form.autobillType === '' ? 'true' : 'false'}
                                update={() => {
                                    update((draft) => {
                                        draft.autobillType = '';
                                    });
                                }}
                                formKey="autobill_none"
                            />
                        )}
                    </div>

                    {errorTags}

                    {bankName && <span className={`${classes.bankName}`}>{bankName}</span>}

                    {form.autobillType === 'dd' && directDebitFields}

                    {form.autobillType === 'cc' && creditCardBlock}
                </>
            ) : (
                <>
                    {/* No direct debit: legacy shows a single "auto bill my card" opt-in that
                        reuses the main payment card. Only the use-payment-details path is offered. */}
                    <div className={`${classes.autobill_type}`}>
                        <CircleCheckbox
                            label={t('wizard:autobillUsePaymentDetails')}
                            // When autobill is required it must stay on, so lock the toggle
                            // checked (matching SimplifiedCheckoutForm).
                            value={autobill.required() || form.autobillType === 'cc' ? 'true' : 'false'}
                            disabled={autobill.required()}
                            formKey="autobill_use_payment_details"
                            update={() => {
                                if (autobill.required()) return;
                                update((draft) => {
                                    const enabling = draft.autobillType !== 'cc';
                                    draft.autobillType = enabling ? 'cc' : '';
                                    draft.autobillUsePaymentDetails = enabling ? 'true' : 'false';
                                });
                            }}
                        />
                    </div>

                    {errorTags}

                    {form.autobillType === 'cc' && (
                        <div className={classes.autobillBox} style={{ marginTop: 16 }}>
                            <div
                                id="custom-location-content-credit-card"
                                dangerouslySetInnerHTML={{
                                    __html: moveinSingle.creditCardContent || '',
                                }}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
